import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin } from 'lucide-react';
import { format, isSameDay } from 'date-fns';
import { Record, RecordStatus } from '../../types/records';

const statusColors: { [key in RecordStatus]: string } = {
  AÇIK: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  KAPALI: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  DİKKAT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
  İPTAL: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

interface BlogCardProps {
    post: Record;
    variants?: any;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, variants }) => {
  const { id, category, status, title, summary, lastUpdated, cityName, districtName, startDate, endDate } = post;
  
  const statusColor = statusColors[status] || statusColors['İPTAL'];
  const location = districtName ? `${districtName}, ${cityName}` : cityName;

  const renderDate = () => {
    if (startDate && endDate) {
      if (isSameDay(startDate, endDate)) {
        return format(startDate, 'dd MMMM yyyy');
      }
      return `${format(startDate, 'dd MMM')} - ${format(endDate, 'dd MMM, yyyy')}`;
    }
    if (startDate) {
      return format(startDate, 'dd MMMM yyyy');
    }
    return `Son Güncelleme: ${format(lastUpdated, 'dd.MM.yyyy')}`;
  };

  return (
    <motion.div 
      variants={variants}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden transform hover:scale-[1.02] hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 flex flex-col"
    >
      <div className="p-6 flex-grow">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">{category}</span>
          <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${statusColor}`}>{status}</span>
        </div>
        
        <h3 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
          <Link to={`/blog/${id}`} className="hover:underline">{title}</Link>
        </h3>
        
        <p className="mt-3 text-gray-600 dark:text-gray-400 text-base">
          {summary}
        </p>
      </div>

      <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center">
            <Calendar size={16} className="mr-1.5" />
            <span>{renderDate()}</span>
          </div>
          <div className="flex items-center">
            <MapPin size={16} className="mr-1.5" />
            <span>{location}</span>
          </div>
        </div>
        
        <div className="mt-4 text-right">
          <Link to={`/blog/${id}`} className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">
            Devamını Gör →
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogCard;
